import React from 'react';
import { Alert, Box, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useTranslation } from 'react-i18next';

/**
 * Format a millisecond duration for display.
 *
 * unit:
 *  - "s"    -> "12.3" (one decimal, second resolution) — used by the 15s test question
 *  - "m:ss" -> "14:59" (minutes and zero-padded seconds) — used by real questions
 */
export const formatRemaining = (ms, unit = 'm:ss') => {
  const clamped = Math.max(0, ms);
  if (unit === 's') {
    return (clamped / 1000).toFixed(1);
  }
  const totalSeconds = Math.ceil(clamped / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Live-recording timer UI shared by TestRecordingView and QuestionView.
 * Shows a pulsing "recording" chip, a warning alert, and a progress bar with
 * "time remaining / total" text.
 */
const RecordingTimerBar = ({
  elapsedMs,
  totalMs,
  unit = 'm:ss',
  warningMessage,
  showChip = true,
}) => {
  const { t } = useTranslation();
  const remainingMs = Math.max(0, totalMs - elapsedMs);
  const progressPercent = Math.min(100, (elapsedMs / totalMs) * 100);
  const unitSuffix = unit === 's' ? 's' : '';
  const barColor = progressPercent >= 90
    ? 'error'
    : progressPercent >= 70
      ? 'warning'
      : 'primary';

  return (
    <>
      {showChip && (
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<FiberManualRecordIcon />}
            label={`${t('candidate.testRecording.recording')} (${formatRemaining(remainingMs, unit)}${unitSuffix})`}
            color="error"
            sx={{ animation: 'pulse 2s infinite' }}
          />
        </Box>
      )}

      {warningMessage && (
        <Alert severity="warning" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1.1rem' }}>
          ⚠️ {warningMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fff3cd' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('candidate.testRecording.timeRemaining')}: {formatRemaining(remainingMs, unit)}{unitSuffix} / {formatRemaining(totalMs, unit)}{unitSuffix}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {progressPercent.toFixed(0)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={barColor}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Paper>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </>
  );
};

export default RecordingTimerBar;
